import React, { useEffect, useState } from 'react'
import ExpansionPanel from '@material-ui/core/ExpansionPanel'
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary'
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails'
import Icon from '@material-ui/core/Icon'
import withStyles from '@material-ui/core/styles/withStyles'
import AzurePipelineMarkets from '~components/page/AzurePipelines/markets'
import MarketCard from '~components/page/AzurePipelines/MarketCard'
import { Select } from '~components/page/AzurePipelines/components/Select'
import { Input } from '~components/page/AzurePipelines/components/Input'
import { Checkbox } from '~components/page/AzurePipelines/components/Checkbox'
import { Radio } from '~components/page/AzurePipelines/components/Radio'
import { Button } from '~components/page/AzurePipelines/components/Button'
import { Toggle } from '~components/page/AzurePipelines/components/Toggle'

// Styled ExpansionPanel components with glassmorphism effect
const StyledExpansionPanel = withStyles({
  root: {
    boxShadow: 'none',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    // backdropFilter: 'blur(10px)',
    borderRadius: '0.75rem',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderBottom: '0',
    borderRight: '0',
    marginBottom: '1rem',
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: '0 0 1rem 0',
    },
  },
  expanded: {},
})(ExpansionPanel)

const StyledExpansionPanelSummary = withStyles({
  root: {
    minHeight: 56,
    '&$expanded': {
      minHeight: 56,
    },
    padding: '1rem',
  },
  content: {
    margin: '0 !important',

    '&$expanded': {
      // margin: '6px 0',
    },
  },
  expanded: {},
})(ExpansionPanelSummary)

const StyledExpansionPanelDetails = withStyles({
  root: {
    padding: '1rem',
    paddingBottom: '1.5rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.3)',
  },
})(ExpansionPanelDetails)

export default function RunNewBuild({ api, open, onClose, setLogs, setTimeline, drawer = false }) {
  const [branch, setBranch] = useState('main')
  const [buildId, setBuildId] = useState('$(Build.BuildId)')
  const [markets, setMarkets] = useState([])
  const [allMarkets, setAllMarkets] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [buildIdDisabled, setBuildIdDisabled] = useState(false)
  const [sortMode, setSortMode] = useState('select')
  const [deploy, setDeploy] = useState(true)
  const [build, setBuild] = useState(false)
  const [env, setEnv] = useState('DEV')
  const [apps, setApps] = useState(['cnx'])
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState(['buildInfo', 'environments', 'apps', 'markets'])
  const [connected, setConnected] = useState(false)
  const [collectStats, setCollectStats] = useState(false)

  const handleChange = panel => (event, isExpanded) => {
    setExpanded(prev => {
      if (isExpanded) {
        return [...prev, panel]
      } else {
        return prev.filter(p => p !== panel)
      }
    })
  }

  const toggleMarket = key =>
    setMarkets(prev => (prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]))

  const toggleBuild = () => {
    const checked = !build
    setBuild(checked)
    setBuildIdDisabled(checked)
    setBuildId(`$(Build.BuildId)`)
  }

  useEffect(() => {
    async function fetchBranches() {
      try {
        const { request } = api.getPipelineRefs()
        const { refs, markets } = await request
        setAllMarkets(markets)
        setBranches(refs)
        setConnected(true) // Set connected to true if API call succeeds
      } catch (e) {
        console.error('Error fetching branches', e)
        setConnected(false) // Set connected to false if API call fails
      } finally {
        setLoading(false)
      }
    }

    fetchBranches()

    // Set up an interval to check connection status periodically
    const connectionCheckInterval = setInterval(async () => {
      try {
        const { request } = api.getPipelineRefs()
        await request
        setConnected(true)
      } catch (e) {
        setConnected(false)
      }
    }, 30000) // Check every 30 seconds

    // Clean up interval on component unmount
    return () => clearInterval(connectionCheckInterval)
  }, [])

  if (!open && drawer) return null

  const runPipeline = async () => {
    setLoading(true)

    const parameters = {
      env,
      build,
      buildID: buildId,
      select: 'Selected Markets',
      deploy: true,
    }

    AzurePipelineMarkets.map(m => Object.assign(parameters, { [m.key]: false }))
    markets.map(item => Object.assign(parameters, { [item]: true }))

    apps.map(app => Object.assign(parameters, { [app]: true }))

    try {
      const { request } = await api.queueBuild({
        data: {
          sourceBranch: branch,
          parameters,
        },
      })

      const result = await request

      const { _links: { web: { href } = {} } = {} } = result
      console.log('Pipeline result:', result)
      if (href?.startsWith('https')) window.open(href)
    } catch (err) {
      alert('Queue error: ' + err)
    }

    setLoading(false)
  }

  // Invert current market selections
  const invertSelection = () => {
    const allKeys = allMarkets.map(m => m.code)
    setMarkets(curr => allKeys.filter(k => !curr.includes(k)))
  }

  const selectAll = () => {
    if (markets.length > 0) {
      setMarkets([])
    }
    const allKeys = allMarkets.map(m => m.code)
    setMarkets(allKeys)
  }

  const clearSelection = () => {
    setMarkets([])
  }

  const component = () => (
    <div className="flex h-full w-full flex-col bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <div className="flex items-center justify-between rounded-lg border-b border-white/20 bg-white/30 p-4 backdrop-blur-sm backdrop-filter">
        <h2 className="text-xl font-semibold text-gray-700">Run New Build</h2>
        <div className="flex items-center space-x-3">
          {/* Connection Status Indicator */}
          <div className="group relative">
            <div
              className={`h-3 w-3 rounded-full ${connected ? 'bg-green' : 'bg-red'} shadow-md transition-all duration-300 ${connected ? 'shadow-green-300/50' : 'shadow-red-300/50'}`}
            >
              {/* Pulse effect when connected */}
              {connected && (
                <span className="bg-green absolute inset-0 animate-ping rounded-full opacity-75"></span>
              )}
            </div>
            {/* Tooltip */}
            <div className="absolute right-0 top-full mt-1 hidden w-32 rounded-md border border-white/30 bg-white/80 px-2 py-1 text-xs text-gray-700 shadow-lg backdrop-blur-sm group-hover:block">
              {connected ? 'Connected to server' : 'Disconnected from server'}
            </div>
          </div>
          {drawer && (
            <button
              onClick={onClose}
              className="rounded-full p-2 text-gray-500 transition-colors hover:bg-white/30 hover:text-gray-700"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <StyledExpansionPanel
          // className={'!shadow-xl'}
          className={'!bg-white/20 !shadow-md !backdrop-blur-lg'}
          expanded={expanded.includes('buildInfo')}
          onChange={handleChange('buildInfo')}
        >
          <StyledExpansionPanelSummary expandIcon={<Icon>expand_more</Icon>}>
            <h3 className="text-lg font-medium text-gray-700">Build Info</h3>
          </StyledExpansionPanelSummary>
          <StyledExpansionPanelDetails>
            <div className="flex w-full flex-col gap-4">
              <div className="mt-4">
                <h4 className="mb-2 text-sm font-medium text-gray-600">Select multiple stages</h4>
                <div className="rounded-lg border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
                  {/*<h5 className="mb-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Checkbox Selection</h5>*/}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {/*BUILD*/}
                    <Checkbox
                      key={`build`}
                      label={'Build'}
                      checked={build}
                      onChange={toggleBuild}
                    />
                    {/*DEPLOY*/}
                    <Checkbox
                      key={`deploy`}
                      label={'Deploy'}
                      checked={deploy}
                      onChange={() => setDeploy(prev => !prev)}
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Select
                  label="Branch"
                  value={branch}
                  onChange={e => setBranch(e.target.value)}
                  options={branches}
                  helperText="Deployment templates branch (not the project source-code)"
                />
                {!build && (
                  <Input
                    label="Build ID (optional)"
                    value={buildId}
                    onChange={e => setBuildId(e.target.value)}
                    disabled={buildIdDisabled}
                    helperText="Use previous build ID to reuse artifacts"
                  />
                )}
              </div>
            </div>
          </StyledExpansionPanelDetails>
        </StyledExpansionPanel>

        {/* Environments Section */}
        <StyledExpansionPanel
          className={'!bg-white/20 !shadow-md !backdrop-blur-lg'}
          elevation={2}
          expanded={expanded.includes('environments')}
          onChange={handleChange('environments')}
        >
          <StyledExpansionPanelSummary expandIcon={<Icon>expand_more</Icon>}>
            <h3 className="text-lg font-medium text-gray-700">Environments</h3>
          </StyledExpansionPanelSummary>
          <StyledExpansionPanelDetails>
            <div className="w-full">
              <h4 className="mb-2 text-sm font-medium text-gray-600">Select an environment</h4>

              {/* Radio buttons for environments */}
              <div className="rounded-lg border border-b-0 border-r-0 border-white/20 bg-white/10 p-4 backdrop-blur-sm">
                {/*<h5 className="mb-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Radio Selection</h5>*/}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {['DEV', 'QA', 'PROD'].map(key => (
                    <Radio
                      key={`radio-${key}`}
                      label={key}
                      value={key}
                      checked={env === key}
                      onChange={() => setEnv(key)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </StyledExpansionPanelDetails>
        </StyledExpansionPanel>

        {/* Apps Section */}
        <StyledExpansionPanel
          className={'!bg-white/20 !shadow-md !backdrop-blur-lg'}
          expanded={expanded.includes('apps')}
          onChange={handleChange('apps')}
        >
          <StyledExpansionPanelSummary expandIcon={<Icon>expand_more</Icon>}>
            <h3 className="text-lg font-medium text-gray-700">Apps</h3>
          </StyledExpansionPanelSummary>
          <StyledExpansionPanelDetails>
            <div className="w-full">
              <h4 className="mb-2 text-sm font-medium text-gray-600">Select apps</h4>
              <div className="rounded-lg border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
                {/*<h5 className="mb-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Checkbox Selection</h5>*/}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {['cnx', 'b2x', 'idp'].map(key => (
                    <Checkbox
                      key={`checkbox-${key}`}
                      label={key.toUpperCase()}
                      checked={apps.includes(key)}
                      onChange={() => {
                        setApps(prev => {
                          if (prev.includes(key)) {
                            return prev.filter(app => app !== key)
                          }
                          return [...prev, key]
                        })
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </StyledExpansionPanelDetails>
        </StyledExpansionPanel>

        {/* Markets Section */}
        {deploy && (
          <StyledExpansionPanel
            className={'!bg-white/20 !shadow-md !backdrop-blur-lg'}
            expanded={expanded.includes('markets')}
            onChange={handleChange('markets')}
          >
            <StyledExpansionPanelSummary expandIcon={<Icon>expand_more</Icon>}>
              <h3 className="text-lg font-medium text-gray-700">Markets</h3>
            </StyledExpansionPanelSummary>
            <StyledExpansionPanelDetails>
              <div className="flex w-full flex-col gap-4">
                <div className="flex flex-col items-start justify-between space-y-4 md:flex-row md:items-center md:space-y-0">
                  <div className="relative w-full md:w-64">
                    <Input
                      placeholder="Search market..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="mb-0"
                    />
                    {search && (
                      <button
                        onClick={() => setSearch('')}
                        className="absolute right-3 top-1/2 z-20 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  <div className="flex w-full flex-wrap items-center gap-2 md:w-auto">
                    <div className="mr-2 flex items-center">
                      <Toggle
                        label="Show health"
                        checked={collectStats}
                        onChange={() => setCollectStats(!collectStats)}
                      />
                    </div>
                    <Button onClick={selectAll} className="text-sm">
                      Select All
                    </Button>
                    <Button onClick={invertSelection} className="text-sm">
                      Invert Selection
                    </Button>
                    <Button onClick={clearSelection} className="text-sm">
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {allMarkets
                    .filter(x => x.host?.endsWith(env.toLowerCase()))
                    .filter(x => !search || x.name?.toLowerCase().includes(search.toLowerCase()))
                    .map(m => (
                      <MarketCard
                        env={env}
                        key={m.code}
                        market={m}
                        collectStats={collectStats}
                        selected={markets.includes(m.code)}
                        onToggle={toggleMarket}
                      />
                    ))}
                </div>
              </div>
            </StyledExpansionPanelDetails>
          </StyledExpansionPanel>
        )}
      </div>

      {/* Footer */}
      <div className="rounded border-t border-white/20 bg-white/30 p-4 backdrop-blur-sm backdrop-filter">
        <Button
          onClick={runPipeline}
          disabled={!build && !deploy}
          pending={loading}
          active={true}
          className="!hover:bg-accent-500 px-8 py-2"
        >
          Run Pipeline
        </Button>
      </div>
    </div>
  )

  return drawer ? (
    <div className={`fixed inset-0 z-50 ${open ? 'visible' : 'invisible'}`}>
      <div
        className={`absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      ></div>
      <div
        className={`absolute right-0 top-0 h-full w-full max-w-3xl transform overflow-hidden bg-transparent transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {component()}
      </div>
    </div>
  ) : (
    component()
  )
}
